import React, { useState } from 'react';
import './ReviewForm.css';
import reviewService from '../services/reviewService';
import { useAuth } from '../context/AuthContext';

const ReviewForm = ({ product, onReviewSubmitted, onCancel }) => {
  const { user, token } = useAuth();
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: '',
    order: product.orderId || null
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.title.trim()) {
      newErrors.title = 'Review title is required';
    } else if (formData.title.length < 5) {
      newErrors.title = 'Title must be at least 5 characters';
    } else if (formData.title.length > 100) {
      newErrors.title = 'Title must be less than 100 characters';
    }
    
    if (!formData.comment.trim()) {
      newErrors.comment = 'Review comment is required';
    } else if (formData.comment.length < 10) {
      newErrors.comment = 'Comment must be at least 10 characters';
    } else if (formData.comment.length > 1000) {
      newErrors.comment = 'Comment must be less than 1000 characters';
    }
    
    if (formData.rating < 1 || formData.rating > 5) {
      newErrors.rating = 'Please select a valid rating';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!user || !token) {
      alert('Please login to submit a review');
      return;
    }
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const reviewData = {
        product: product._id,
        rating: formData.rating,
        title: formData.title.trim(),
        comment: formData.comment.trim(),
        ...(formData.order && { order: formData.order })
      };
      
      const response = await reviewService.createReview(reviewData, token);
      
      if (response.success) {
        alert('Review submitted successfully!');
        onReviewSubmitted && onReviewSubmitted(response.data);
        resetForm();
      }
    } catch (error) {
      console.error('Error submitting review:', error);
      alert(error.message || 'Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      rating: 5,
      title: '',
      comment: '',
      order: product.orderId || null
    });
    setErrors({});
  };

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <button
        key={index}
        type="button"
        className={`star-btn ${index < formData.rating ? 'active' : ''}`}
        onClick={() => handleInputChange('rating', index + 1)}
      >
        <i className="fas fa-star"></i>
      </button>
    ));
  };

  if (!user) {
    return (
      <div className="review-form-login-required">
        <div className="login-prompt">
          <i className="fas fa-lock"></i>
          <h3>Login Required</h3>
          <p>Please login to write a review for this product.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="review-form-container">
      <div className="review-form-header">
        <h3>Write a Review</h3>
        <p>Share your experience with this product</p>
      </div>

      <form onSubmit={handleSubmit} className="review-form">
        <div className="form-group">
          <label className="form-label">Your Rating *</label>
          <div className="rating-input">
            {renderStars()}
            <span className="rating-text">
              {formData.rating === 1 && 'Poor'}
              {formData.rating === 2 && 'Fair'}
              {formData.rating === 3 && 'Good'}
              {formData.rating === 4 && 'Very Good'}
              {formData.rating === 5 && 'Excellent'}
            </span>
          </div>
          {errors.rating && <span className="error-message">{errors.rating}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Review Title *</label>
          <input
            type="text"
            className={`form-input ${errors.title ? 'error' : ''}`}
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            placeholder="Summarize your review in a few words"
            maxLength="100"
          />
          <div className="char-count">
            {formData.title.length}/100 characters
          </div>
          {errors.title && <span className="error-message">{errors.title}</span>}
        </div>

        <div className="form-group">
          <label className="form-label">Your Review *</label>
          <textarea
            className={`form-textarea ${errors.comment ? 'error' : ''}`}
            value={formData.comment}
            onChange={(e) => handleInputChange('comment', e.target.value)}
            placeholder="Tell others about your experience with this product..."
            rows="5"
            maxLength="1000"
          />
          <div className="char-count">
            {formData.comment.length}/1000 characters
          </div>
          {errors.comment && <span className="error-message">{errors.comment}</span>}
        </div>

        {product.orderId && (
          <div className="verified-purchase-info">
            <i className="fas fa-check-circle"></i>
            <span>This review will be marked as verified purchase</span>
          </div>
        )}

        <div className="form-actions">
          <button
            type="button"
            className="btn-cancel"
            onClick={onCancel}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-submit"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <i className="fas fa-spinner fa-spin"></i>
                Submitting...
              </>
            ) : (
              <>
                <i className="fas fa-paper-plane"></i>
                Submit Review
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ReviewForm;
